import React, { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { useParams } from "react-router-dom";
import ChatPanel from "./ChatPanel";
import useChat from "../hooks/useChat";

interface LayoutWithChatProps {
  children: React.ReactNode;
}

const LayoutWithChat: React.FC<LayoutWithChatProps> = ({
  children,
}) => {
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const params = useParams();
  const leagueId = params.leagueId;
  const playerName = params.playerName;
  const { messages: chatMessages, sendMessage, newChat, currentThought, chats, selectChat, chatId } = useChat(leagueId, playerName);
  const greeting = { id: 'greeting', text: 'How can I help you today?', isUser: false } as const;
  const baseMessages = chatMessages.length > 0
    ? chatMessages.map((m, idx) => ({ id: String(idx), text: m.content, isUser: m.role === 'user' }))
    : [greeting];
  // Only show thinking if we have actual content and the last message was from user
  const shouldShowThinking = Boolean(currentThought) && 
    baseMessages.length > 0 && 
    baseMessages[baseMessages.length - 1].isUser;
  const mappedMessages = shouldShowThinking
    ? [...baseMessages, { id: 'thinking', text: currentThought as string, isUser: false, isThinking: true } as any]
    : baseMessages;

  const handleSendMessage = (message: string) => {
    sendMessage(message);
  };

  // Prevent body scroll when mobile chat is open
  useEffect(() => {
    if (isMobileChatOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Add styles to prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileChatOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-bg-primary">
      {/* Mobile Chat Button - Only visible on mobile when chat is closed */}
      {!isMobileChatOpen && (
        <button
          onClick={() => setIsMobileChatOpen(true)}
          className="lg:hidden fixed top-3 right-16 z-[60] bg-transparent hover:bg-bg-tertiary text-text-primary p-2 rounded-lg transition-colors"
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Mobile Chat Overlay */}
      <div className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${isMobileChatOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileChatOpen(false)} />
        <div 
          className={`absolute inset-0 w-full bg-bg-primary shadow-xl transform transition-transform duration-300 ease-in-out ${
            isMobileChatOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ height: '100dvh' }}
          onClick={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {/* Mobile Chat Panel with close button */}
          <div className="h-full">
             <ChatPanel
               title="Ask Fantasy Mind AI"
               messages={mappedMessages}
               onSendMessage={handleSendMessage}
               className="h-full"
               isMobile={true}
               chats={chats}
               currentChatId={chatId}
               onSelectChat={selectChat}
               onNewChat={() => newChat(leagueId, playerName)}
               onClose={() => setIsMobileChatOpen(false)}
             />
          </div>
        </div>
      </div>

      <main className="flex-grow p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="flex flex-col gap-6 lg:col-span-2 lg:min-h-[100vh]">
            {children}
          </div>

          {/* Desktop Chat Interface - Right sidebar with sticky positioning, hidden on mobile */}
          <div className="hidden lg:block">
            <ChatPanel
              title="Ask Fantasy Mind AI"
              messages={mappedMessages}
              onSendMessage={handleSendMessage}
              chats={chats}
              currentChatId={chatId}
              onSelectChat={selectChat}
              onNewChat={() => newChat(leagueId, playerName)}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default LayoutWithChat;
